import { useState } from 'react'
import { UtilityProgram } from '../../../../types/utilityProgram'
import mockUtilityProgram from '../../../../types/utilityProgram/mock'
import useMockableViewModel from '../../../../hooks/useMockableViewModel'
import { useAppDispatch, useAppSelector } from '../../../../hooks'
import { selectUtilityProgramEnrollmentForUser } from '../../../../reducers/utilityProgramEnrollments'
import {
  selectUtilityProgramForUserUtility,
  selectUtilityProgramLogoUrl,
} from '../../../../reducers/utilityPrograms'
import {
  createDeviceEnrollmentsForUserSelector,
  selectProgramViewConfigFromAllSources,
} from '@/selectors'
import {
  behavioralEnrollmentCollection,
  chargerEnrollmentCollection,
  energySystemEnrollmentCollection,
  thermostatEnrollmentCollection,
  vehicleEnrollmentCollection,
} from '@/reducers/deviceProgramEnrollments'
import { ID } from '@/types/model'
import { useHistory } from 'react-router-dom'
import {
  BehavioralEnrollment,
  ChargerEnrollment,
  DeviceEnrollment,
  EnergySystemEnrollment,
  ThermostatEnrollment,
  VehicleEnrollment,
} from '@/types/utilityProgramEnrollment'
import { logEvent } from '@/logging'
import useProfileId from '@/hooks/useProfileId'
import { RSAAResultAction } from 'redux-api-middleware'

type Props = {
  vehicleId?: ID
  thermostatId?: ID
  chargerId?: ID
  energySystemId?: string
  deviceType:
    | 'vehicle'
    | 'thermostat'
    | 'vehicle_charger'
    | 'behavioral'
    | 'energySystem'
  isEnrolledValue?: boolean
  onEnrollToggleValueChange?: (checked: boolean) => void
}

function isChargerEnrollment(
  enrollment:
    | VehicleEnrollment
    | ThermostatEnrollment
    | ChargerEnrollment
    | BehavioralEnrollment
    | EnergySystemEnrollment,
): enrollment is ChargerEnrollment {
  return 'charger_id' in enrollment
}

function useViewModel(props: Props) {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const profileId = useProfileId()

  // Fetch all our device type enrollments to make sure we have the latest data
  // if we invalidated the cache elsewhere
  vehicleEnrollmentCollection.useFetch()
  thermostatEnrollmentCollection.useFetch()
  chargerEnrollmentCollection.useFetch()
  behavioralEnrollmentCollection.useFetch()
  energySystemEnrollmentCollection.useFetch()

  const utilityProgramEnrollment = useAppSelector(
    selectUtilityProgramEnrollmentForUser,
  )
  const maybeUtilityProgram: UtilityProgram | undefined = useAppSelector(
    selectUtilityProgramForUserUtility,
  )
  const utilityProgram =
    utilityProgramEnrollment?.utility_program ?? maybeUtilityProgram

  const deviceEnrollment = useAppSelector((state) => {
    const id = createDeviceEnrollmentsForUserSelector(props.deviceType)(
      state,
    ).find((enrollment) => {
      if (props.deviceType === 'vehicle') {
        return (enrollment as VehicleEnrollment).vehicle_id === props.vehicleId
      }
      if (props.deviceType === 'thermostat') {
        return (
          (enrollment as ThermostatEnrollment).thermostat_id ===
          props.thermostatId
        )
      }
      if (props.deviceType === 'behavioral') {
        return (enrollment as BehavioralEnrollment).profile_id === profileId
      }
      if (props.deviceType === 'energySystem') {
        return (
          (enrollment as EnergySystemEnrollment).energy_system_id ===
          props.energySystemId
        )
      }
      return (
        isChargerEnrollment(enrollment) &&
        enrollment.charger_id === props.chargerId
      )
    })?.id
    if (!id) {
      return undefined
    }

    if (props.deviceType === 'thermostat') {
      return thermostatEnrollmentCollection.selectors.selectById(state, id)
    }

    if (props.deviceType === 'vehicle_charger') {
      return chargerEnrollmentCollection.selectors.selectById(state, id)
    }

    if (props.deviceType === 'behavioral') {
      return behavioralEnrollmentCollection.selectors.selectById(state, id)
    }

    if (props.deviceType === 'energySystem') {
      return energySystemEnrollmentCollection.selectors.selectById(state, id)
    }

    return vehicleEnrollmentCollection.selectors.selectById(state, id)
  })

  const logoUrl =
    useAppSelector((state) => {
      const utilityProgramUrl = selectUtilityProgramLogoUrl(state)
      const deviceEnrollmentLogoUrl =
        deviceEnrollment?.view_config_json?.logoUrl
      return deviceEnrollmentLogoUrl ?? utilityProgramUrl
    }) ?? utilityProgram?.logo_url

  const viewConfig = useAppSelector(
    (state) =>
      selectProgramViewConfigFromAllSources(state, props.vehicleId)?.scheduler,
  )

  const [disenrollConfirmationOpen, setDisenrollConfirmationOpen] =
    useState(false)
  const closeDisenrollConfirmation = () => setDisenrollConfirmationOpen(false)

  const [isEnrolledInternal, setIsEnrolledInternal] = useState(
    !!deviceEnrollment && !deviceEnrollment.unenrolled_at,
  )
  const isEnrolled = props.isEnrolledValue ?? isEnrolledInternal
  const setIsEnrolled = props.onEnrollToggleValueChange ?? setIsEnrolledInternal

  const [enrollError, setEnrollError] = useState<string | undefined>(undefined)

  const [unenrollSurveyOpen, setUnenrollSurveyOpen] = useState(false)
  const closeSurvey = () => {
    closeDisenrollConfirmation()
    setUnenrollSurveyOpen(false)
  }

  const setEnrollment = (enrolled: boolean) => {
    setIsEnrolled(enrolled)
    let promise
    if (!enrolled && !deviceEnrollment) {
      return
    }
    if (!deviceEnrollment) {
      if (props.deviceType === 'thermostat') {
        promise = dispatch(
          thermostatEnrollmentCollection.actions.create({
            utility_program_id: utilityProgram?.id,
            thermostat_id: props.thermostatId,
          }),
        )
      } else if (props.deviceType === 'vehicle_charger') {
        promise = dispatch(
          chargerEnrollmentCollection.actions.create({
            utility_program_id: utilityProgram?.id,
            charger_id: props.chargerId,
          }),
        )
      } else if (props.deviceType === 'behavioral' && profileId !== null) {
        promise = dispatch(
          behavioralEnrollmentCollection.actions.create({
            utility_program_id: utilityProgram?.id,
            profile_id: profileId,
          }),
        )
      } else if (props.deviceType === 'vehicle') {
        promise = dispatch(
          vehicleEnrollmentCollection.actions.create({
            utility_program_id: utilityProgram?.id,
            vehicle_id: props.vehicleId,
          }),
        )
      } else if (props.deviceType === 'energySystem') {
        promise = dispatch(
          energySystemEnrollmentCollection.actions.create({
            utility_program_id: utilityProgram?.id,
            energy_system_id: props.energySystemId,
          }),
        )
      }
    } else {
      const unenrolledAt = enrolled ? null : new Date().toISOString()
      if (props.deviceType === 'thermostat') {
        promise = dispatch(
          thermostatEnrollmentCollection.actions.update(deviceEnrollment.id, {
            unenrolled_at: unenrolledAt,
          }),
        )
      } else if (props.deviceType === 'vehicle_charger') {
        promise = dispatch(
          chargerEnrollmentCollection.actions.update(deviceEnrollment.id, {
            unenrolled_at: unenrolledAt,
          }),
        )
      } else if (props.deviceType === 'behavioral') {
        promise = dispatch(
          behavioralEnrollmentCollection.actions.update(deviceEnrollment.id, {
            unenrolled_at: unenrolledAt,
          }),
        )
      } else if (props.deviceType === 'energySystem') {
        promise = dispatch(
          energySystemEnrollmentCollection.actions.update(deviceEnrollment.id, {
            unenrolled_at: unenrolledAt,
          }),
        )
      } else {
        promise = dispatch(
          vehicleEnrollmentCollection.actions.update(deviceEnrollment.id, {
            unenrolled_at: unenrolledAt,
          }),
        )
      }
    }

    promise.then((res: RSAAResultAction<DeviceEnrollment>) => {
      if (res.type.toString().includes('FAILURE')) {
        if ('name' in res.payload && res.payload.name === 'ApiError') {
          setEnrollError(res.payload?.message)
        } else {
          setEnrollError('An error has occurred. Please try again.')
        }
      } else {
        setEnrollError(undefined)
        if (enrolled) {
          logEvent('utilityenrollment', {
            metadata: { program: utilityProgram?.name ?? 'undefined' },
          })
        }
      }
    })
  }

  const onEnrollToggleChange = (checked: boolean) => {
    if (viewConfig?.disenrollConfirmation && !checked) {
      setDisenrollConfirmationOpen(true)
      return
    }
    setEnrollment(checked)

    if (!checked) {
      setUnenrollSurveyOpen(true)
    }
  }

  const onButtonClick = () => {
    history.push('/utility-program-enrollment')
  }

  const confirmDisenroll = () => {
    setEnrollment(false)
    setDisenrollConfirmationOpen(false)
    setUnenrollSurveyOpen(true)
  }

  const isMultiProgram =
    utilityProgram?.view_config_json?.useMultiProgramFlow ?? false

  return {
    title: viewConfig?.title ?? '',
    description: viewConfig?.description ?? '',
    programLogoUrl: logoUrl,
    isEnrolled,
    onEnrollToggleChange,
    buttonConfig: viewConfig?.button,
    onButtonClick,
    enrolledAlert: isEnrolled ? viewConfig?.enrolledAlert : undefined,
    disenrollConfirmationConfig: viewConfig?.disenrollConfirmation,
    disenrollConfirmationOpen,
    closeDisenrollConfirmation,
    confirmDisenroll,
    unenrollSurveyOpen,
    closeSurvey,
    enrollError,
    isMultiProgram,
    includeHeader:
      viewConfig?.title !== '' &&
      viewConfig?.title !== undefined &&
      viewConfig?.title !== null,
  }
}

function useMockViewModel() {
  const viewConfig = mockUtilityProgram.view_config_json.scheduler

  const [isEnrolled, setIsEnrolled] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const onEnrollToggleChange = (checked: boolean) => {
    setIsEnrolled(checked)
    setModalOpen(!checked)
  }
  const [enrollError] = useState<string | undefined>(undefined)

  return {
    title: viewConfig.title,
    description: viewConfig.description,
    programLogoUrl: mockUtilityProgram.logo_url,
    isEnrolled,
    onEnrollToggleChange,
    onButtonClick: () => {},
    buttonConfig: undefined,
    enrolledAlert: isEnrolled ? viewConfig?.enrolledAlert : undefined,
    disenrollConfirmationConfig: viewConfig?.disenrollConfirmation,
    disenrollConfirmationOpen: modalOpen,
    closeDisenrollConfirmation: () => setModalOpen(false),
    confirmDisenroll: () => {},
    enrollError,
    unenrollSurveyOpen: false,
    closeSurvey: () => alert('Survey closed!'),
    isMultiProgram: false,
    includeHeader: true,
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
